// "This file is too big" pop-up (Phelipe 2026-10-05). Photo/video and site
// walk uploads stop at 5 GB; a bigger file gets this pop-up with the limit
// and how to get it raised: email support or the support chat (Intercom).
// Built in JS like lib/toast.js. Closes on the backdrop, OK or Escape.
//
// Usage:
//   import { UPLOAD_LIMIT_BYTES, showSizeLimitModal } from "../lib/size_limit_modal"
//   if (file.size > UPLOAD_LIMIT_BYTES) return showSizeLimitModal(file, this.sizeLimitTextValue, { source: "photos", project_id: 1 })
//
// The third argument rides along on the Mixpanel events ("Upload Size Limit
// Hit", "... Chat Opened"), so we can see how often 5 GB is too little.
import { track } from "./mixpanel"

// 5 GB is also the most one storage upload can carry.
export const UPLOAD_LIMIT_BYTES = 5 * 1024 * 1024 * 1024
const LIMIT_LABEL = "5 GB"
const SUPPORT_EMAIL = "support@conslog.com"

const ICON = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'

// Server-translated words (ApplicationHelper#upload_size_limit_text), English
// if a page didn't pass them.
const DEFAULT_TEXT = {
  title: "This file is too big",
  body: "__NAME__ is __SIZE__. Uploads here can be up to __LIMIT__.",
  raise: "To raise the limit, email us at __EMAIL__ or message us in the chat below.",
  chat: "Message us",
  close: "OK"
}

function sizeLabel(bytes) {
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export function showSizeLimitModal(file, text = {}, context = {}) {
  const words = { ...DEFAULT_TEXT, ...text }
  const props = { ...context, size_gb: file ? Math.round(file.size / (1024 * 1024 * 1024) * 10) / 10 : null, limit_gb: 5 }
  track("Upload Size Limit Hit", props)
  document.querySelector("[data-size-limit-modal]")?.remove()

  const overlay = document.createElement("div")
  overlay.dataset.sizeLimitModal = ""
  overlay.className = "fixed inset-0 flex items-center justify-center p-4 bg-black/50"
  overlay.style.zIndex = "100" // above the upload pop-up it opens from
  overlay.innerHTML = `
    <div class="bg-white rounded-xl shadow-xl w-full max-w-md" role="dialog" aria-modal="true">
      <div class="p-6 text-center">
        <div class="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">${ICON}</div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2" data-role="title"></h3>
        <p class="text-[13px] text-gray-600" data-role="body"></p>
        <p class="text-[13px] text-gray-600 mt-2" data-role="raise"></p>
      </div>
      <div class="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
        <button type="button" data-role="chat" class="px-4 py-2 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"></button>
        <button type="button" data-role="close" class="px-4 py-2 text-[13px] font-medium text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer transition-colors"></button>
      </div>
    </div>`

  const panel = overlay.firstElementChild
  panel.querySelector("[data-role='title']").textContent = words.title
  // Function replacements, so a "$" in a file name stays as typed.
  panel.querySelector("[data-role='body']").textContent = words.body
    .replace("__NAME__", () => file?.name || "")
    .replace("__SIZE__", () => (file ? sizeLabel(file.size) : ""))
    .replace("__LIMIT__", () => LIMIT_LABEL)

  // The email becomes a link; the rest stays plain text.
  const raise = panel.querySelector("[data-role='raise']")
  const [before, after = ""] = words.raise.split("__EMAIL__")
  const link = document.createElement("a")
  link.href = `mailto:${SUPPORT_EMAIL}`
  link.className = "text-brand font-medium hover:underline"
  link.textContent = SUPPORT_EMAIL
  raise.append(before, link, after)

  const close = () => {
    document.removeEventListener("keydown", onKey, true)
    overlay.remove()
  }
  // Caught on the way down, so Escape closes this pop-up and not also the
  // upload box under it (the Site Walks one closes on Escape too).
  const onKey = (e) => {
    if (e.key !== "Escape") return
    e.preventDefault()
    e.stopPropagation()
    close()
  }
  document.addEventListener("keydown", onKey, true)
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })

  const chatBtn = panel.querySelector("[data-role='chat']")
  if (window.Intercom) {
    chatBtn.textContent = words.chat
    chatBtn.addEventListener("click", () => {
      track("Upload Size Limit Chat Opened", props)
      close()
      window.Intercom("show")
    })
  } else {
    chatBtn.remove() // no chat on this page: the email link still works
  }

  const okBtn = panel.querySelector("[data-role='close']")
  okBtn.textContent = words.close
  okBtn.addEventListener("click", close)

  document.body.appendChild(overlay)
  okBtn.focus()
}
